// src/hooks/useCharts.js
import { useCallback, useMemo, useRef, useState } from 'react';
import { getGrafico } from '../infra/api/GraficoApi';
import { interpretarErroGrafico } from '../pages/Historico/utils/chartErrors';

export function useCharts(expId) {
    const [charts, setCharts] = useState({});
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // Codigo do erro, separado da mensagem: `errors` continua string porque ja e
    // renderizado direto como texto em duas telas. O codigo permite a UI distinguir
    // "consolidando" (estado transitorio) de falha de verdade.
    const [errorCodes, setErrorCodes] = useState({});
    const inFlight = useRef(new Set());

    // ✅ CORREÇÃO: `charts` foi removido da lista de dependências do useCallback
    const load = useCallback(
        async (tipo, options = {}) => {
            const { force = false } = options;

            // Acessa o valor mais recente de `charts` diretamente do `setCharts`
            let chartExists = false;
            setCharts((currentCharts) => {
                chartExists = !!currentCharts[tipo];
                return currentCharts;
            });

            if (
                !expId ||
                !tipo ||
                inFlight.current.has(tipo) ||
                (!force && chartExists)
            ) {
                return false;
            }

            inFlight.current.add(tipo);
            setLoading(true);
            setErrors((prev) => {
                const next = { ...prev };
                delete next[tipo];
                return next;
            });
            setErrorCodes((prev) => {
                const next = { ...prev };
                delete next[tipo];
                return next;
            });

            try {
                const data = await getGrafico(expId, tipo);
                // ✅ CORREÇÃO: A atualização de estado já garante o acesso ao valor mais recente
                setCharts((prev) => ({ ...prev, [tipo]: data }));
                return true;
            } catch (e) {
                // A explicacao real vem no corpo da resposta; `e.message` do axios e
                // sempre "Request failed with status code NNN".
                const { codigo, mensagem } = interpretarErroGrafico(e);
                setErrors((prev) => ({ ...prev, [tipo]: mensagem }));
                setErrorCodes((prev) => ({ ...prev, [tipo]: codigo }));
                return false;
            } finally {
                inFlight.current.delete(tipo);
                // Uma verificação mais segura para o estado de loading
                setTimeout(() => {
                    if (inFlight.current.size === 0) {
                        setLoading(false);
                    }
                }, 0);
            }
        },
        [expId] // A única dependência real é o ID do experimento
    );

    const prefetch = useCallback(
        (tipos) => {
            tipos.forEach((t) => load(t));
        },
        [load]
    );

    return useMemo(
        () => ({ charts, errors, errorCodes, loading, load, prefetch }),
        [charts, errors, errorCodes, loading, load, prefetch]
    );
}
